import { generateCalendar, isDateInRange, countOverlappingDays } from './calendar';
import styles from './calendar.module.css';

interface CalendarEvent {
    title: string;
    description?: string;
    type?: string;
    onclick?: ($month, $day) => void;
    startDate: string;
    endDate: string;
    id: string;
}

export class CalendarApp {
    private events: CalendarEvent[] = [];  // 日付ごとのイベント管理
    private calendarElement: HTMLElement;
    private selectedDate: string;
    private onclick?: (day) => void;;
    private weeks: string[][];
    private month: string = '';
    private size: number = 4;

    constructor(elementId: string, month: string, size: number=4) {
        const element = document.getElementById(elementId);
        this.size = size;
        if (!element) {
            throw new Error(`Element with id "${elementId}" not found.`);
        }
        this.initialize(month);
        this.calendarElement = element;
    }

    public initialize(date: string): void {
        this.month = date;
        const year = new Date(date).getFullYear();
        const month = new Date(date).getMonth() + 1;
        this.weeks = generateCalendar(year, month);
    }

    // イベント追加メソッド
    addEvent(date: string, event: CalendarEvent): void {
        const year = new Date(date).getFullYear();
        const month = new Date(date).getMonth() + 1;
        const day = year + '/' + month + '/' + (new Date(date).getDate());
        event.startDate = day;
        event.id = crypto.randomUUID();
        this.events.push(event);
    }

    // イベント取得メソッド
    getEvents(date: string): CalendarEvent[] {
        let result: CalendarEvent[] = [];
        this.events.forEach((e) => {
            if (isDateInRange(date, e.startDate, e.endDate)) {
                result.push(e);
            }
        });
        return result;
    }

    getWeekEvents(week: string[]): CalendarEvent[] {
        let result: Record<string, CalendarEvent> = {};
 
        week.forEach((d) => {
            this.events.forEach((e) => {
                if (isDateInRange(d, e.startDate, e.endDate)) {
                    result[e.id] = e;
                }
            });
        });
        return Object.values(result);
    }

    public getHeader(): string {
        const daysOfWeek = ['日', '月', '火', '水', '木', '金', '土'];
        let html = '';
        daysOfWeek.forEach((day, i) => {
            let cls = styles.header;
            if (i==0) {
                cls += ' '+styles.red;
            } else if(i==6) {
                cls += ' '+styles.blue;
            }
            html += `<th class="${cls}">${day}</th>`;
        });
        html += '</tr>';
        return html;
    }

    private getWeekSchedules(week: string[], stage: number): Record<string, CalendarEvent[]> {
        let sched: Record<string, CalendarEvent[]>  = {};
        let events = this.getWeekEvents(week);
        let used_events: string[] = [];
        let firstDay: string = '';
        week.forEach(d => {
            if (! firstDay) {
                firstDay = d;
            }
            for (let i=0; i < stage; i++) {
                events.forEach(e => {
                    if (sched[d]?.[i]) return;
                    if (used_events.includes(e.id)) return;
                    if ((d==firstDay && isDateInRange(d, e.startDate, e.endDate)) || (d!=firstDay && isDateInRange(d, e.startDate))) {
                        week.forEach(d2 => {
                            if (isDateInRange(d2, e.startDate, e.endDate)) {
                                if (! sched[d2]) {
                                    sched[d2] = [];
                                }
                                sched[d2][i] = e;
                            }
                        });
                        used_events.push(e.id);
                    }
                });
            }
        });
        return sched;
    }
    
    public getWeek(week: string[]): string {
        let html = '';
        let range = this.size;
        let schedule: Record<string, CalendarEvent[]> = this.getWeekSchedules(week, range);

        for(let i=-1; i<range; i++) {
            html += '<tr>';
            let multiEventDays = 0;
            week.forEach(day => {
                let sched = schedule?.[day]?.[i];
                if (multiEventDays>1) {
                    multiEventDays -= 1
                    return;
                } else if (multiEventDays==1) {
                    multiEventDays = 0;
                }
                if (sched?.endDate) {
                    multiEventDays = countOverlappingDays(week, sched.startDate, sched.endDate);
                }
                // let cls = styles.cell;
                let cls = styles.cell;

                if (this.selectedDate==day) {
                    cls += ' ' + styles.selected;
                }
                let content = '';
                if (i==-1) {
                    const year = new Date().getFullYear();
                    const month = new Date().getMonth() + 1;
                    const now = year + '/' + month + '/' + (new Date().getDate());
                    let d = day.split('/')[2];
                    cls += ' ' + styles.cell_top;
                    content = `<div><span class="${now==day ? styles.current_date : ''}">${d}</span></div>`;
                } else {
                    cls += ' '; 
                    cls += (i==(range-1)) 
                        ? styles.cell_bottom
                        : styles.cell_middle;
                    if (sched?.type) {
                        cls += ' ' + styles?.[sched.type];
                    }
                    if (sched?.title) {
                        content = sched?.title ?? '';
                    }
                }
                if (multiEventDays) {
                    cls += ' ' + styles.cell_multi;
                }
                html += `<td data-date="${day}" class="${cls}" colspan="${multiEventDays ?? 1}">`;
                html += content;
                html += `</td>`;
            });
            html += '</tr>';
        }
        return html;
    }

    // private getDay(day: string, event?: CalendarEvent, last: boolean = false): string {

    //     let html = '';
    //     if (event.description) {
    //     } 
    //     return html;
    // }

    // カレンダー描画メソッド
    public render(): void {
        const calendarElement = document.getElementById('calendar');
        if (!calendarElement) return;
    
        let html = `<table class="${styles.calendar}"><tr>`;
        html += this.getHeader();
    
        this.weeks.forEach(week => {
            html += this.getWeek(week);
        });
    
        html += '</table>';
        calendarElement.innerHTML = html;
        this.addClickEventListener();
    }
    
    public selected(date): void {
        const year = new Date(date).getFullYear();
        const month = new Date(date).getMonth() + 1;
        const day = year + '/' + month + '/' + (new Date(date).getDate());
        this.selectedDate = day;
        // this.renderMonthCalendar();
    }
    
    public addClickEventListener(): void {
        const dateCells = document.querySelectorAll('[data-date]') as NodeListOf<HTMLDivElement>;
        dateCells.forEach(cell => {
            cell.addEventListener('click', () => {
                const date = cell.getAttribute('data-date');
                if (this.onclick) {
                    this.onclick(date);
                }
            });
        });
    }

    public onClick(clickDay?: (day) => void): void {
        this.onclick = clickDay;
    }
    

}


// グローバルスコープに関数を追加（Bladeから呼び出すため）
// (window as any).renderMonthCalendar = renderMonthCalendar;

(window as any).CalendarApp = CalendarApp;